import {
  ICPaasConfig,
  TextWidgetOptions,
  TextWidgetHandle,
  MessageResponse,
  TextMessage,
} from './types';
import type { ApiError, ChatHistoryTurn, WebchatRequest, WebchatResponse } from './protocol';
import { AuthManager } from './auth';
import { EventEmitter } from './events';

/*
 * The server keeps at most the last 20 history entries, 4,000 chars each and
 * 24,000 in all (normaliseHistory in server/agents.ts). The same budget is
 * applied here, not just left to the server: /webhook/webchat rejects bodies
 * over 64 KB, and twenty untrimmed long turns would blow past that and turn
 * every later message into a 413.
 */
const MAX_HISTORY_TURNS = 20;
const MAX_TURN_CHARS = 4_000;
const MAX_HISTORY_CHARS = 24_000;

const BRAND_GRADIENT = 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)';

/**
 * A non-2xx answer from the text endpoint. `status` lets a caller tell a bad
 * key (401) or unknown agent (404) from a provider outage (502).
 */
export class ChatRequestError extends Error {
  readonly status: number;

  constructor(message: string, status: number) {
    super(message);
    this.name = 'ChatRequestError';
    this.status = status;
  }
}

/**
 * A rolling conversation: the turns sent as `history` with the next message.
 * Only completed exchanges are recorded — a message that failed never reached
 * the model, so replaying it would describe a conversation that did not happen.
 */
export class Conversation {
  private turns: ChatHistoryTurn[] = [];
  /** Server-side transcript id; adopted from the first reply, cleared on reset. */
  id: string | null = null;

  /** A copy, so a caller cannot mutate what the next request sends. */
  get history(): ChatHistoryTurn[] {
    return this.turns.slice();
  }

  record(userText: string, reply: string): void {
    if (!reply) return;
    this.turns.push(
      { role: 'user', content: userText.slice(0, MAX_TURN_CHARS) },
      { role: 'assistant', content: reply.slice(0, MAX_TURN_CHARS) },
    );
    this.turns = this.turns.slice(-MAX_HISTORY_TURNS);

    // Drop from the front so the most recent turns survive the budget.
    let total = this.turns.reduce((n, t) => n + t.content.length, 0);
    while (this.turns.length > 0 && total > MAX_HISTORY_CHARS) {
      total -= this.turns.shift()!.content.length;
    }
  }

  reset(): void {
    this.turns = [];
    this.id = null;
  }
}

/** What a visitor sees when a message fails. The raw error goes to the console. */
function describeError(error: unknown): string {
  if (error instanceof ChatRequestError) {
    switch (error.status) {
      case 401:
      case 403:
        return 'This chat is not set up correctly (the API key was rejected).';
      case 404:
        return 'This assistant is not available right now.';
      case 429:
        return 'Too many messages. Please wait a moment and try again.';
      default:
        return error.message || 'Something went wrong. Please try again.';
    }
  }
  return 'Could not reach the assistant. Check your connection and try again.';
}

const CHAT_ICON =
  '<svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" ' +
  'stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 ' +
  '2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>';
const CLOSE_ICON =
  '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" ' +
  'stroke-linecap="round" aria-hidden="true"><path d="M18 6 6 18M6 6l12 12"/></svg>';

/*
 * Styles for the floating launcher. They live in a shadow root so the host
 * page's CSS cannot restyle the widget and the widget's cannot leak out.
 */
const LAUNCHER_CSS = `
  .launcher {
    width: 56px; height: 56px; border-radius: 50%; border: none; cursor: pointer;
    background: ${BRAND_GRADIENT}; color: #fff;
    display: flex; align-items: center; justify-content: center;
    box-shadow: 0 6px 20px rgba(0, 0, 0, 0.2);
  }
  .launcher:focus-visible { outline: 3px solid #a5b4fc; outline-offset: 2px; }
  .panel {
    position: absolute; right: 0; bottom: 72px;
    width: min(360px, calc(100vw - 40px));
    height: min(540px, calc(100vh - 112px));
    border-radius: 12px; box-shadow: 0 12px 40px rgba(0, 0, 0, 0.25);
    display: none;
  }
  .panel.open { display: block; }
  .close {
    background: transparent; border: none; color: inherit; cursor: pointer;
    padding: 0; line-height: 0; opacity: 0.9;
  }
  .close:focus-visible { outline: 2px solid #fff; outline-offset: 2px; }
`;

/** The parts of a built chat UI its mount function needs. */
interface ChatUI {
  container: HTMLElement;
  input: HTMLInputElement;
}

/**
 * Text chat manager - handles text widgets and messaging
 */
export class TextManager {
  private config: ICPaasConfig;
  private auth: AuthManager;
  private events: EventEmitter;
  private widgets = new Set<TextWidgetHandle>();
  /** The conversation behind ai.sendMessage(); widgets each keep their own. */
  private conversation = new Conversation();

  constructor(config: ICPaasConfig, auth: AuthManager, events: EventEmitter) {
    this.config = config;
    this.auth = auth;
    this.events = events;
  }

  /**
   * Create and mount a text widget: inside `targetElement` when given,
   * otherwise as a floating launcher in the bottom-right corner.
   */
  createWidget(options: TextWidgetOptions): TextWidgetHandle {
    const handle = options.targetElement != null
      ? this.mountInline(options, options.targetElement)
      : this.mountFloating(options);

    this.widgets.add(handle);
    this.log(`Text widget created (${options.targetElement != null ? 'inline' : 'floating'})`);
    return handle;
  }

  /**
   * Send a text message on the client-level conversation: each call carries
   * the previous exchanges as history until resetConversation() is called.
   */
  async sendMessage(text: string, agentId?: string): Promise<MessageResponse> {
    return this.send(text, agentId, this.conversation);
  }

  /** Forget the history behind sendMessage(). Widgets are unaffected. */
  resetConversation(): void {
    this.conversation.reset();
  }

  /**
   * Destroy every widget this manager created
   */
  destroy(): void {
    this.widgets.forEach(w => w.destroy());
  }

  // ==================== Private ====================

  private async send(text: string, agentId: string | undefined, conversation: Conversation): Promise<MessageResponse> {
    const history = conversation.history;
    const body: WebchatRequest = {
      text,
      visitorId: this.generateVisitorId(),
      ...(agentId ? { agentId } : {}),
      ...(history.length ? { history } : {}),
      ...(conversation.id ? { conversationId: conversation.id } : {}),
    };

    const response = await fetch(`${this.auth.getBaseURL()}/webhook/webchat`, {
      method: 'POST',
      // Bearer, not just the cookie: on a customer's domain the cookie is
      // stripped as cross-site and every message would come back 401.
      headers: this.auth.headers({ 'Content-Type': 'application/json' }),
      credentials: this.auth.credentialsMode(),
      body: JSON.stringify(body),
    });

    // A non-JSON error body (a proxy's HTML 502, say) used to throw an opaque
    // parse error instead of the status.
    const data = (await response.json().catch(() => null)) as WebchatResponse | ApiError | null;

    if (!response.ok || data?.ok === false) {
      throw new ChatRequestError((data?.ok === false && data.error) || `Failed to send message: ${response.status}`, response.status);
    }
    if (!data) throw new ChatRequestError('Malformed response from server', response.status);

    const replies = (data.messages ?? []).map(m => m.text).filter(Boolean);
    conversation.record(text, replies.join('\n\n'));
    // Keep the server's transcript id so the whole chat is stored as one
    // conversation rather than one per message.
    if (!conversation.id && data.conversationId) conversation.id = data.conversationId;

    replies.forEach(reply => {
      this.events.emit('message', { channel: 'text', role: 'bot', text: reply, ...(agentId ? { agentId } : {}) });
    });
    return data;
  }

  private mountInline(options: TextWidgetOptions, target: string | HTMLElement): TextWidgetHandle {
    const element = typeof target === 'string' ? document.querySelector<HTMLElement>(target) : target;
    if (!element) {
      throw new Error(`Target element not found: ${String(target)}`);
    }

    const conversation = new Conversation();
    const ui = this.buildChat(options, conversation);
    element.appendChild(ui.container);

    const handle: TextWidgetHandle = {
      open: () => {},
      close: () => {},
      resetConversation: () => conversation.reset(),
      destroy: () => {
        ui.container.remove();
        this.widgets.delete(handle);
      },
    };
    return handle;
  }

  private mountFloating(options: TextWidgetOptions): TextWidgetHandle {
    const host = document.createElement('div');
    host.className = 'icpaas-chat-launcher';
    // Inline so page selectors like `body > div` cannot move or restyle it;
    // `all: initial` stops inherited fonts and colours reaching the shadow tree.
    host.style.cssText = `
      all: initial;
      position: fixed;
      right: 20px;
      bottom: 20px;
      z-index: 2147483000;
      font-family: system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif;
      font-size: 14px;
      line-height: 1.4;
    `;
    // attachShadow exists in every browser the SDK supports; the fallback only
    // costs style isolation, not the widget.
    const root: ShadowRoot | HTMLElement = host.attachShadow ? host.attachShadow({ mode: 'open' }) : host;

    const style = document.createElement('style');
    style.textContent = LAUNCHER_CSS;
    root.appendChild(style);

    const panel = document.createElement('div');
    panel.className = 'panel';
    panel.setAttribute('role', 'dialog');
    panel.setAttribute('aria-label', options.title || 'Chat Support');

    const launcher = document.createElement('button');
    launcher.type = 'button';
    launcher.className = 'launcher';

    let isOpen = false;
    const setOpen = (open: boolean) => {
      isOpen = open;
      panel.classList.toggle('open', open);
      launcher.innerHTML = open ? CLOSE_ICON : CHAT_ICON;
      launcher.setAttribute('aria-label', open ? 'Close chat' : 'Open chat');
      launcher.setAttribute('aria-expanded', String(open));
      if (open) ui.input.focus();
    };

    const conversation = new Conversation();
    const ui = this.buildChat(options, conversation, () => {
      setOpen(false);
      launcher.focus();
    });
    panel.appendChild(ui.container);
    panel.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        setOpen(false);
        launcher.focus();
      }
    });

    launcher.onclick = () => setOpen(!isOpen);
    setOpen(false);

    root.appendChild(panel);
    root.appendChild(launcher);
    document.body.appendChild(host);

    const handle: TextWidgetHandle = {
      open: () => setOpen(true),
      close: () => setOpen(false),
      resetConversation: () => conversation.reset(),
      destroy: () => {
        host.remove();
        this.widgets.delete(handle);
      },
    };
    return handle;
  }

  /**
   * Build the chat UI (header, transcript, input). Inline styles, so the
   * inline widget looks the same as it always has; the floating one wraps
   * this in its shadow root.
   */
  private buildChat(options: TextWidgetOptions, conversation: Conversation, onClose?: () => void): ChatUI {
    const container = document.createElement('div');
    container.className = 'icpaas-text-widget';
    container.style.cssText = `
      width: 100%;
      height: 100%;
      display: flex;
      flex-direction: column;
      border: 1px solid #e5e7eb;
      border-radius: 12px;
      overflow: hidden;
      background: white;
      box-sizing: border-box;
    `;

    // Create header
    const header = document.createElement('div');
    header.style.cssText = `
      padding: 16px;
      background: ${BRAND_GRADIENT};
      color: white;
      font-weight: 600;
      font-size: 16px;
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 8px;
    `;
    const title = document.createElement('span');
    title.textContent = options.title || 'Chat Support';
    header.appendChild(title);
    if (onClose) {
      const close = document.createElement('button');
      close.type = 'button';
      close.className = 'close';
      close.setAttribute('aria-label', 'Close chat');
      close.innerHTML = CLOSE_ICON;
      close.onclick = onClose;
      header.appendChild(close);
    }
    container.appendChild(header);

    // Create messages area
    const messagesArea = document.createElement('div');
    messagesArea.className = 'messages-area';
    messagesArea.setAttribute('aria-live', 'polite');
    messagesArea.style.cssText = `
      flex: 1;
      overflow-y: auto;
      padding: 16px;
      display: flex;
      flex-direction: column;
      gap: 12px;
    `;
    container.appendChild(messagesArea);

    // Add welcome message if provided
    if (options.welcomeMessage) {
      this.addMessage(messagesArea, {
        role: 'bot',
        text: options.welcomeMessage,
        timestamp: Date.now(),
      });
    }

    // Create input area
    const inputArea = document.createElement('div');
    inputArea.style.cssText = `
      padding: 16px;
      border-top: 1px solid #e5e7eb;
      display: flex;
      gap: 8px;
    `;

    const input = document.createElement('input');
    input.type = 'text';
    input.placeholder = 'Type your message...';
    input.setAttribute('aria-label', 'Message');
    input.style.cssText = `
      flex: 1;
      min-width: 0;
      padding: 10px 14px;
      border: 1px solid #e5e7eb;
      border-radius: 8px;
      font-size: 14px;
      outline: none;
    `;

    const sendButton = document.createElement('button');
    sendButton.type = 'button';
    sendButton.textContent = 'Send';
    sendButton.style.cssText = `
      padding: 10px 20px;
      background: ${BRAND_GRADIENT};
      color: white;
      border: none;
      border-radius: 8px;
      font-weight: 600;
      cursor: pointer;
      font-size: 14px;
    `;

    const sendMessage = async () => {
      const text = input.value.trim();
      if (!text) return;

      // Add user message to UI
      this.addMessage(messagesArea, {
        role: 'user',
        text,
        timestamp: Date.now(),
      });

      input.value = '';
      input.disabled = true;
      sendButton.disabled = true;

      try {
        const response = await this.send(text, options.agentId, conversation);

        // Add bot responses
        (response.messages ?? []).forEach(msg => {
          this.addMessage(messagesArea, msg);
          options.onMessage?.(msg);
        });
      } catch (error) {
        console.error('[ICPaaS SDK] Failed to send message:', error);
        this.events.emit('error', error instanceof Error ? error : new Error(String(error)));
        this.addMessage(messagesArea, { role: 'bot', text: describeError(error), timestamp: Date.now() }, true);
      } finally {
        input.disabled = false;
        sendButton.disabled = false;
        input.focus();
      }
    };

    sendButton.onclick = sendMessage;
    // keypress is deprecated and never fires for some IMEs/soft keyboards.
    input.onkeydown = (e) => {
      if (e.key === 'Enter' && !e.shiftKey && !e.isComposing) {
        e.preventDefault();
        sendMessage();
      }
    };

    inputArea.appendChild(input);
    inputArea.appendChild(sendButton);
    container.appendChild(inputArea);

    return { container, input };
  }

  /**
   * Add a message to the messages area
   */
  private addMessage(container: HTMLElement, message: TextMessage, isError = false): void {
    const messageEl = document.createElement('div');
    const look = isError
      ? 'background: #fef2f2; color: #991b1b; border: 1px solid #fecaca; align-self: flex-start;'
      : message.role === 'user'
        ? `background: ${BRAND_GRADIENT}; color: white; align-self: flex-end; margin-left: auto;`
        : 'background: #f3f4f6; color: #1f2937; align-self: flex-start;';
    messageEl.style.cssText = `
      padding: 10px 14px;
      border-radius: 12px;
      max-width: 75%;
      word-wrap: break-word;
      white-space: pre-wrap;
      ${look}
    `;
    if (isError) messageEl.setAttribute('role', 'alert');
    messageEl.textContent = message.text;
    container.appendChild(messageEl);
    container.scrollTop = container.scrollHeight;
  }

  /**
   * Generate a unique visitor ID
   */
  private generateVisitorId(): string {
    // localStorage throws (not returns null) in some privacy modes, so both the
    // read and the write have to be guarded.
    try {
      const stored = localStorage.getItem('icpaas-visitor-id');
      if (stored) return stored;
      const id = crypto.randomUUID?.() ?? Math.floor(Math.random() * 1e15).toString();
      localStorage.setItem('icpaas-visitor-id', id);
      return id;
    } catch {
      return crypto.randomUUID?.() ?? Math.floor(Math.random() * 1e15).toString();
    }
  }

  private log(message: string): void {
    if (this.config.debug) console.log(`[ICPaaS SDK] ${message}`);
  }
}
